import React, { useState } from 'react'
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client'
import {
  Box, Typography, Paper, Stack, Button, TextField,
  CircularProgress, Drawer, Modal, InputAdornment,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import SendIcon from '@mui/icons-material/Send'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import OrderComponent from '../components/Order/Order'
import { getOrderById as GET_ORDER_BY_ID_QUERY } from '../apollo/queries'

/* ─── GQL ────────────────────────────────────────────────────────────────── */
const GET_ORDER_BY_ID = gql`${GET_ORDER_BY_ID_QUERY}`

const RESTAURANT_TICKETS = gql`
  query RestaurantSupportTickets($status: TicketStatus, $restaurantId: Int) {
    restaurantSupportTickets(status: $status, restaurantId: $restaurantId) {
      id ticketNumber orderId orderDisplayId issueType status assignedQueue description evidenceUrls createdAt resolvedAt updatedAt
      customer { id name email phone }
      refundRequest {
        id requestedAmount approvedAmount status restaurantDecision affectedItems
        faultParty customerChoice creditScope creditTotal bonusAmount
        walletRefundAmount cardRefundAmount
      }
      messages { id senderType senderName message isInternal issueType evidenceUrls createdAt isRead }
    }
  }
`

const RESTAURANT_REPLY = gql`
  mutation RestaurantReplyTicket($ticketId: ID!, $message: String!, $isInternal: Boolean) {
    restaurantReplyTicket(ticketId: $ticketId, message: $message, isInternal: $isInternal) {
      id senderType senderName message isInternal createdAt
    }
  }
`

const MARK_RESTAURANT_READ = gql`
  mutation MarkRestaurantTicketRead($ticketId: ID!) {
    markRestaurantTicketRead(ticketId: $ticketId)
  }
`

/* ─── Constants ──────────────────────────────────────────────────────────── */
const STATUS_META = {
  // Old rows only: restaurants stopped deciding refunds on 2026-09-30.
  AWAITING_RESTAURANT: { label: 'Under Review',  bg: '#f5f3ff', border: '#8b5cf6', text: '#6d28d9', dot: '#8b5cf6', priority: 2 },
  OPEN:                { label: 'Open',            bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8', dot: '#3b82f6', priority: 1 },
  AWAITING_ADMIN:      { label: 'Under Review',    bg: '#f5f3ff', border: '#8b5cf6', text: '#6d28d9', dot: '#8b5cf6', priority: 2 },
  AWAITING_CUSTOMER:   { label: 'Awaiting Customer', bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8', dot: '#3b82f6', priority: 2 },
  RESOLVED:            { label: 'Resolved',        bg: '#f0fdf4', border: '#16a34a', text: '#15803d', dot: '#16a34a', priority: 3 },
  CLOSED_NO_ACTION:    { label: 'Closed',          bg: '#f8fafc', border: '#cbd5e1', text: '#64748b', dot: '#94a3b8', priority: 4 },
}

const ISSUE_LABELS = {
  MISSING_ITEM: 'Missing Item', INCORRECT_ITEM: 'Incorrect Item',
  WRONG_ORDER: 'Wrong Order', FOOD_QUALITY: 'Food Quality',
  NEVER_ARRIVED: 'Never Arrived', WRONG_ADDRESS_DROP: 'Wrong Drop Address',
  DRIVER_ISSUE: 'Driver Issue', ORDER_LATE: 'Order Late',
  TECH_BILLING: 'Tech/Billing', OTHER: 'Other',
}

const FILTERS = [
  { key: undefined,             label: 'All' },
  { key: 'OPEN',                label: 'Open' },
  { key: 'AWAITING_ADMIN',      label: 'Under Review' },
  { key: 'AWAITING_CUSTOMER',   label: 'Awaiting Customer' },
  { key: 'RESOLVED',            label: 'Resolved' },
  { key: 'CLOSED_NO_ACTION',    label: 'Closed' },
]

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

function timeAgo(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return days < 30 ? `${days}d ago` : `${Math.floor(days / 30)}mo ago`
}

/* ─── Status Badge ───────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.OPEN
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.1, py: 0.35, borderRadius: '20px',
      bgcolor: m.bg, border: `1px solid ${m.border}`, flexShrink: 0,
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: m.dot }} />
      <Typography fontSize="0.69rem" fontWeight={700} color={m.text} sx={{ whiteSpace: 'nowrap' }}>
        {m.label}
      </Typography>
    </Box>
  )
}

/* ─── Summary Bar ────────────────────────────────────────────────────────── */
function SummaryBar({ tickets }) {
  const open    = tickets.filter(t => t.status === 'OPEN').length
  const review  = tickets.filter(t => t.status === 'AWAITING_ADMIN' || t.status === 'AWAITING_RESTAURANT').length
  const customer = tickets.filter(t => t.status === 'AWAITING_CUSTOMER').length
  const resolved = tickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED_NO_ACTION').length

  const items = [
    { label: 'Open',          value: open,     color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
    { label: 'Under Review',  value: review,   color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe' },
    { label: 'Awaiting Customer', value: customer, color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
    { label: 'Resolved',      value: resolved, color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  ]

  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2.5}>
      {items.map(it => (
        <Box key={it.label} sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          px: 2, py: 1.25, borderRadius: '12px',
          bgcolor: it.bg, border: `1px solid ${it.border}`,
        }}>
          <Typography fontSize="1.4rem" fontWeight={800} color={it.color} lineHeight={1}>
            {it.value}
          </Typography>
          <Typography fontSize="0.75rem" color={it.color} fontWeight={500} lineHeight={1.3}>
            {it.label}
          </Typography>
        </Box>
      ))}
    </Stack>
  )
}

/* ─── Ticket Row ─────────────────────────────────────────────────────────── */
function TicketRow({ ticket, onClick, isLast }) {
  const unread = (ticket.messages || []).filter(m => (m.senderType === 'ADMIN' || m.senderType === 'CUSTOMER') && !m.isRead).length
  const isUrgent = ticket.status === 'AWAITING_RESTAURANT'
  const refund = ticket.refundRequest

  return (
    <Box
      onClick={() => onClick(ticket)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '140px 90px 1fr 180px 100px 130px 90px',
        alignItems: 'center',
        gap: 1,
        px: 2.5, py: 1.75,
        cursor: 'pointer',
        borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
        borderLeft: isUrgent ? '3px solid #f97316' : '3px solid transparent',
        bgcolor: isUrgent ? '#fffaf5' : '#fff',
        transition: 'background 0.12s ease',
        '&:hover': { bgcolor: isUrgent ? '#fff3e6' : '#f8fafc' },
      }}
    >
      {/* Ticket # */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography fontSize="0.85rem" fontWeight={700} color="#1d4ed8" sx={{ whiteSpace: 'nowrap' }}>
          {ticket.ticketNumber}
        </Typography>
        {unread > 0 && (
          <Box sx={{
            bgcolor: '#ef4444', borderRadius: '10px',
            px: 0.7, py: 0.15, display: 'flex', alignItems: 'center',
          }}>
            <Typography fontSize="0.62rem" fontWeight={800} color="#fff">{unread} new</Typography>
          </Box>
        )}
      </Box>

      {/* Order # */}
      <Typography fontSize="0.85rem" color="#475569">{ticket.orderDisplayId || `#${ticket.orderId}`}</Typography>

      {/* Issue + description */}
      <Box sx={{ minWidth: 0 }}>
        <Typography fontSize="0.85rem" fontWeight={600} color="#0f172a" noWrap>
          {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
        </Typography>
        {ticket.description && (
          <Typography fontSize="0.75rem" color="#94a3b8" noWrap sx={{ mt: 0.15 }}>
            {ticket.description}
          </Typography>
        )}
      </Box>

      {/* Status */}
      <Box><StatusBadge status={ticket.status} /></Box>

      {/* Requested */}
      <Typography fontSize="0.85rem" color={refund ? '#0f172a' : '#94a3b8'} fontWeight={refund ? 600 : 400}>
        {refund ? `$${parseFloat(refund.requestedAmount).toFixed(2)}` : '—'}
      </Typography>

      {/* Submitted */}
      <Typography fontSize="0.78rem" color="#64748b">{fmt(ticket.createdAt)}</Typography>

      {/* Last updated */}
      <Typography fontSize="0.78rem" color="#94a3b8">
        {timeAgo(ticket.updatedAt || ticket.createdAt)}
      </Typography>
    </Box>
  )
}

/* ─── Ticket Drawer ──────────────────────────────────────────────────────── */
function TicketDrawer({ ticket, open, onClose, onRefetch }) {
  const [orderModalOpen, setOrderModalOpen] = useState(false)
  const [fetchOrder, { data: orderData, loading: orderLoading }] = useLazyQuery(GET_ORDER_BY_ID)

  const [replyText, setReplyText] = useState('')
  const [localMessages, setLocalMessages] = useState([])
  const messagesEndRef = React.useRef(null)

  React.useEffect(() => {
    setLocalMessages(ticket?.messages || [])
  }, [ticket?.id])

  React.useEffect(() => {
    const serverCount = (ticket?.messages || []).filter(m => !String(m.id).startsWith('tmp-')).length
    const localCount = localMessages.filter(m => !String(m.id).startsWith('tmp-')).length
    if (serverCount > localCount) setLocalMessages(ticket.messages)
  }, [ticket?.messages?.length]) // eslint-disable-line

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [localMessages.length])

  const [markRead] = useMutation(MARK_RESTAURANT_READ)

  // Auto-mark admin messages as read when drawer opens
  React.useEffect(() => {
    if (open && ticket?.id) {
      const hasUnread = (ticket.messages || []).some(m => (m.senderType === 'ADMIN' || m.senderType === 'CUSTOMER') && !m.isRead)
      if (hasUnread) {
        markRead({ variables: { ticketId: ticket.id } }).then(() => onRefetch())
      }
    }
  }, [open, ticket?.id]) // eslint-disable-line

  const [sendReply, { loading: replying }] = useMutation(RESTAURANT_REPLY, {
    onCompleted: () => onRefetch(),
  })

  const handleSend = (isInternal) => {
    const text = replyText.trim()
    if (!text) return
    setLocalMessages(prev => [...prev, {
      id: `tmp-${Date.now()}`, senderType: 'RESTAURANT', senderName: 'You',
      message: text, isInternal, isRead: true, createdAt: new Date().toISOString(),
    }])
    setReplyText('')
    sendReply({ variables: { ticketId: ticket.id, message: text, isInternal } })
  }

  if (!ticket) return null

  const refund = ticket.refundRequest
  const effectiveStatus = ticket.status
  const isResolved = effectiveStatus === 'RESOLVED'
  const isClosed = effectiveStatus === 'CLOSED_NO_ACTION'
  const canReply = !isResolved && !isClosed
  const money = v => `$${(parseFloat(v) || 0).toFixed(2)}`
  const approved = refund?.approvedAmount != null && refund.status !== 'DENIED' ? parseFloat(refund.approvedAmount) : null
  const restaurantPays = refund?.faultParty === 'RESTAURANT'

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 520 }, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', border: 'none', borderLeft: '1px solid #e2e8f0' } }}>

      {/* Drawer header */}
      <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
              <Typography fontWeight={800} fontSize="1rem" color="#0f172a">{ticket.ticketNumber}</Typography>
              <StatusBadge status={effectiveStatus} />
            </Stack>
            <Typography fontSize="0.78rem" color="#64748b">
              Order {ticket.orderDisplayId || `#${ticket.orderId}`} · {ticket.customer?.name || '—'} · Submitted {fmt(ticket.createdAt)}
            </Typography>
          </Box>
          <Box
            onClick={onClose}
            sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', '&:hover': { bgcolor: '#f1f5f9', color: '#475569' } }}
          >×</Box>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>

        {/* Ticket summary */}
        <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.5 }}>
            Ticket Summary
          </Typography>
          <Stack direction="row" spacing={3} mb={1.5} flexWrap="wrap">
            <Box>
              <Typography fontSize="0.72rem" color="#94a3b8" mb={0.3}>Issue</Typography>
              <Typography fontSize="0.88rem" fontWeight={700} color="#0f172a">
                {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
              </Typography>
            </Box>
            <Box>
              <Typography fontSize="0.72rem" color="#94a3b8" mb={0.3}>Customer</Typography>
              <Typography fontSize="0.88rem" fontWeight={700} color="#0f172a">
                {ticket.customer?.name || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography fontSize="0.72rem" color="#94a3b8" mb={0.3}>Order</Typography>
              <Typography
                fontSize="0.88rem" fontWeight={700} color="#1d4ed8"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => { fetchOrder({ variables: { id: ticket.orderId } }); setOrderModalOpen(true) }}
              >
                {ticket.orderDisplayId || `#${ticket.orderId}`} ↗
              </Typography>
            </Box>
          </Stack>

          {ticket.description && (
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', px: 1.5, py: 1.25, mb: 1.25, borderLeft: '3px solid #e2e8f0' }}>
              <Typography fontSize="0.72rem" color="#94a3b8" mb={0.4}>Customer said</Typography>
              <Typography fontSize="0.83rem" color="#334155" sx={{ fontStyle: 'italic' }}>
                &quot;{ticket.description}&quot;
              </Typography>
            </Box>
          )}

          {refund && (() => {
            let items = null
            try { items = refund.affectedItems ? (typeof refund.affectedItems === 'string' ? JSON.parse(refund.affectedItems) : refund.affectedItems) : null } catch {}
            return (
              <Box sx={{ bgcolor: '#fffbeb', borderRadius: '8px', p: 1.5, border: '1px solid #fde68a' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontSize="0.72rem" color="#92400e" mb={0.2}>Refund Requested</Typography>
                    <Typography fontSize="1.1rem" fontWeight={800} color="#92400e">
                      ${parseFloat(refund.requestedAmount).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '8px', px: 1.25, py: 0.6, border: '1px solid #fde68a' }}>
                    <Typography fontSize="0.7rem" fontWeight={700} color="#92400e">
                      {refund.status?.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                </Stack>
                {items?.length > 0 && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #fde68a' }}>
                    {items.map((item, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" mb={0.4}>
                        <Box>
                          <Typography fontSize="0.82rem" fontWeight={600} color="#0f172a">{item.name}</Typography>
                          <Typography fontSize="0.72rem" color="#94a3b8">Ordered: {item.qty} · Missing: {item.refundQty}</Typography>
                        </Box>
                        <Typography fontSize="0.82rem" fontWeight={700} color="#92400e">${parseFloat(item.refundAmount).toFixed(2)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            )
          })()}
        </Box>

        {/* Conversation */}
        <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.5 }}>
            Conversation
          </Typography>

          {/* Initial report */}
          <Box sx={{ bgcolor: '#faf5ff', borderRadius: '10px', p: 1.5, border: '1px solid #e9d5ff', mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
              <Typography fontSize="0.72rem" fontWeight={700} color="#7c3aed">
                {ticket.customer?.name || 'Customer'} · Initial report
              </Typography>
              <Typography fontSize="0.7rem" color="#94a3b8">{fmt(ticket.createdAt)}</Typography>
            </Stack>
            <Typography fontSize="0.85rem" color="#334155">
              <strong>Issue:</strong> {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
            </Typography>
            {ticket.description && (
              <Typography fontSize="0.85rem" color="#334155" mt={0.3}>
                <strong>Description:</strong> {ticket.description}
              </Typography>
            )}
            {refund && (
              <Typography fontSize="0.85rem" color="#334155" mt={0.3}>
                <strong>Requested:</strong> ${parseFloat(refund.requestedAmount).toFixed(2)} refund
              </Typography>
            )}
          </Box>

          {/* Messages */}
          {localMessages.filter(m => !m.isInternal).length === 0 && localMessages.filter(m => m.isInternal).length === 0 && (
            <Typography fontSize="0.82rem" color="#94a3b8" textAlign="center" py={1}>No messages yet</Typography>
          )}
          <Stack spacing={1.5}>
            {localMessages.map(m => {
              const isAdmin = m.senderType === 'ADMIN'
              const isCustomer = m.senderType === 'CUSTOMER'
              const isRestaurant = m.senderType === 'RESTAURANT'
              const internal = m.isInternal
              const bg = internal ? '#fff7ed' : isAdmin ? '#eff6ff' : isCustomer ? '#faf5ff' : '#f0fdf4'
              const borderColor = internal ? '#fed7aa' : isAdmin ? '#bfdbfe' : isCustomer ? '#e9d5ff' : '#bbf7d0'
              const nameColor = internal ? '#c2410c' : isAdmin ? '#1d4ed8' : isCustomer ? '#7c3aed' : '#15803d'
              const displayName = isRestaurant ? 'You' : m.senderName || (isAdmin ? 'RushMyFood Support' : isCustomer ? ticket.customer?.name || 'Customer' : 'Restaurant')
              const evidenceUrls = m.evidenceUrls
                ? (Array.isArray(m.evidenceUrls) ? m.evidenceUrls : (() => { try { return JSON.parse(m.evidenceUrls) } catch { return [] } })())
                : []
              return (
                <Box key={m.id} sx={{
                  p: 1.5, borderRadius: '10px', bgcolor: bg,
                  border: `1px solid ${borderColor}`,
                  opacity: String(m.id).startsWith('tmp-') ? 0.55 : 1,
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography fontSize="0.72rem" fontWeight={700} color={nameColor}>{displayName}</Typography>
                      {internal && (
                        <Box sx={{ bgcolor: '#fed7aa', borderRadius: '6px', px: 0.75, py: 0.1 }}>
                          <Typography fontSize="0.6rem" fontWeight={700} color="#9a3412">internal</Typography>
                        </Box>
                      )}
                    </Stack>
                    <Typography fontSize="0.7rem" color="#94a3b8">{fmt(m.createdAt)}</Typography>
                  </Stack>
                  <Typography fontSize="0.85rem" color="#0f172a" sx={{ whiteSpace: 'pre-line' }}>
                    {m.message}{String(m.id).startsWith('tmp-') ? ' (sending…)' : ''}
                  </Typography>
                  {evidenceUrls.length > 0 && (
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                      {evidenceUrls.map((url, i) => (
                        <Box key={i} component="a" href={url} target="_blank" rel="noreferrer">
                          <Box component="img" src={url} sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '6px', border: `1px solid ${borderColor}`, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              )
            })}
          </Stack>
          <div ref={messagesEndRef} />
        </Box>

        {/* Reply */}
        {canReply && (
          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25 }}>
              Send Message
            </Typography>
            <TextField
              fullWidth multiline rows={3} size="small"
              placeholder="Type your message…"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              sx={{ mb: 1.25, '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#f8fafc', fontSize: '0.88rem' } }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" size="small"
                endIcon={replying ? null : <SendIcon sx={{ fontSize: '0.95rem !important' }} />}
                disabled={!replyText.trim() || replying}
                onClick={() => handleSend(false)}
                sx={{ flex: 1, borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, py: 1 }}
              >
                {replying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Reply to Customer'}
              </Button>
              <Button
                variant="outlined" size="small"
                disabled={!replyText.trim() || replying}
                onClick={() => handleSend(true)}
                sx={{ flex: 1, borderRadius: '8px', fontWeight: 700, fontSize: '0.78rem', textTransform: 'none', borderColor: '#f97316', color: '#c2410c', py: 1, '&:hover': { bgcolor: '#fff7ed', borderColor: '#f97316' } }}
              >
                🔒 Message Admin
              </Button>
            </Stack>
            <Typography fontSize="0.7rem" color="#94a3b8" mt={0.75}>
              &quot;Reply to Customer&quot; is visible to everyone · &quot;Message Admin&quot; is internal only
            </Typography>
          </Box>
        )}

        {/* ── Refund: RushMyFood decides, the restaurant sees it ── */}
        {refund && (
          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25 }}>
              Refund
            </Typography>
            {approved == null && !isClosed && (
              <Typography fontSize="0.84rem" color="#334155">
                RushMyFood is reviewing this refund. You can reply to the customer or message RushMyFood above.
              </Typography>
            )}
            {approved != null && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                  <AccountBalanceWalletIcon sx={{ color: '#16a34a', fontSize: '1.1rem' }} />
                  <Typography fontWeight={700} color="#15803d" fontSize="0.9rem">
                    {money(approved)} refund approved by RushMyFood
                  </Typography>
                </Stack>
                <Typography fontSize="0.8rem" color="#334155" mb={0.5}>
                  {refund.status === 'AWAITING_CUSTOMER' && 'The customer is choosing between their original payment and credit.'}
                  {refund.customerChoice === 'ORIGINAL' && 'Refunded to the customer\'s original payment.'}
                  {refund.customerChoice === 'CREDIT' && (refund.creditScope === 'RESTAURANT'
                    ? `Given as ${money(refund.creditTotal)} store credit at your restaurant${refund.bonusAmount > 0 ? ` (includes ${money(refund.bonusAmount)} bonus)` : ''}. It comes out of your payout when the customer spends it here.`
                    : `Given as ${money(refund.creditTotal)} RushMyFood credit.`)}
                </Typography>
                <Typography fontSize="0.8rem" fontWeight={600} color={restaurantPays ? '#c2410c' : '#475569'}>
                  {restaurantPays ? 'Found to be the restaurant\'s mistake, so your restaurant pays this refund.' : 'RushMyFood pays this refund.'}
                </Typography>
                {isResolved && ticket.resolvedAt && (
                  <Typography fontSize="0.74rem" color="#94a3b8" mt={0.5}>Resolved {fmt(ticket.resolvedAt)}</Typography>
                )}
              </>
            )}
            {isClosed && approved == null && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
                <Typography fontWeight={600} color="#64748b" fontSize="0.9rem">No refund given — ticket closed</Typography>
              </Stack>
            )}
          </Box>
        )}
        {!refund && isResolved && (
          <Box sx={{ bgcolor: '#f0fdf4', borderRadius: '12px', border: '1px solid #86efac', p: 2, mb: 2 }}>
            <Typography fontWeight={700} color="#15803d" fontSize="0.9rem">Ticket resolved</Typography>
            <Typography fontSize="0.78rem" color="#15803d">Resolved {fmt(ticket.resolvedAt)}</Typography>
          </Box>
        )}
        {!refund && isClosed && (
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: '#94a3b8', fontSize: '1.1rem' }} />
              <Typography fontWeight={600} color="#64748b" fontSize="0.9rem">Ticket closed</Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Order modal */}
      <Modal open={orderModalOpen} onClose={() => setOrderModalOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 1400 }}>
        <Box sx={{ bgcolor: 'white', borderRadius: 2, width: { xs: '100%', sm: '85%', md: '65%', lg: '50%' }, maxHeight: '90vh', overflowY: 'auto', outline: 'none' }}>
          {orderLoading && <Box p={4} textAlign="center"><CircularProgress /></Box>}
          {!orderLoading && orderData?.getOrderById && (
            <OrderComponent order={orderData.getOrderById} onClose={() => setOrderModalOpen(false)} onStatusChange={() => {}} availableRiders={[]} />
          )}
          {!orderLoading && !orderData?.getOrderById && (
            <Box p={4} textAlign="center"><Typography color="text.secondary">Order not found.</Typography></Box>
          )}
        </Box>
      </Modal>
    </Drawer>
  )
}

/* ─── Table Header ───────────────────────────────────────────────────────── */
function TableHeader() {
  const cols = ['Ticket #', 'Order', 'Issue', 'Status', 'Requested', 'Submitted', 'Last Updated']
  const widths = ['140px', '90px', '1fr', '180px', '100px', '130px', '90px']
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: widths.join(' '),
      gap: 1, px: 2.5, py: 1.25,
      bgcolor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      borderRadius: '14px 14px 0 0',
    }}>
      {cols.map(c => (
        <Typography key={c} fontSize="0.72rem" fontWeight={700} color="#94a3b8" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {c}
        </Typography>
      ))}
    </Box>
  )
}

/* ─── Main View ──────────────────────────────────────────────────────────── */
export default function RestaurantTickets() {
  const [statusFilter, setStatusFilter] = useState(undefined)
  const [search, setSearch] = useState('')
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  // Optimistically track which tickets have been opened (badge cleared immediately)
  const [readTicketIds, setReadTicketIds] = useState(new Set())

  const rawRestaurantId = localStorage.getItem('restaurantId')
  const restaurantId = rawRestaurantId ? parseInt(rawRestaurantId) : undefined

  // Filtered query — drives the table
  const { data, loading, refetch } = useQuery(RESTAURANT_TICKETS, {
    variables: { status: statusFilter, restaurantId: restaurantId || undefined },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })

  // Unfiltered query — drives the summary bar + filter pill counts (always all tickets)
  const { data: countsData, refetch: refetchCounts } = useQuery(RESTAURANT_TICKETS, {
    variables: { restaurantId: restaurantId || undefined },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })

  const allTickets = data?.restaurantSupportTickets || []
  const allTicketsUnfiltered = countsData?.restaurantSupportTickets || []

  // Derive selected ticket from live data
  const selectedTicket = selectedTicketId ? (allTickets.find(t => t.id === selectedTicketId) || allTicketsUnfiltered.find(t => t.id === selectedTicketId) || null) : null

  // Sort: action-needed → open → under review → resolved → closed, then newest first
  const sorted = [...allTickets].sort((a, b) => {
    const pa = STATUS_META[a.status]?.priority ?? 99
    const pb = STATUS_META[b.status]?.priority ?? 99
    if (pa !== pb) return pa - pb
    return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  })

  const tickets = search.trim()
    ? sorted.filter(t =>
        t.ticketNumber.toLowerCase().includes(search.toLowerCase()) ||
        String(t.orderId).includes(search.trim()) ||
        (ISSUE_LABELS[t.issueType] || '').toLowerCase().includes(search.toLowerCase())
      )
    : sorted

  const actionCount = allTicketsUnfiltered.filter(t => t.status === 'AWAITING_RESTAURANT').length

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', py: 4, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>

        {/* Page header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <SupportAgentIcon sx={{ color: '#1d4ed8', fontSize: 26 }} />
              <Typography fontSize="1.5rem" fontWeight={800} color="#0f172a" letterSpacing="-0.3px">
                Support Tickets
              </Typography>
              {actionCount > 0 && (
                <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '20px', px: 1.25, py: 0.4 }}>
                  <Typography fontSize="0.72rem" fontWeight={700} color="#dc2626">
                    {actionCount} need action
                  </Typography>
                </Box>
              )}
            </Stack>
            <Typography fontSize="0.85rem" color="#64748b">
              Customer complaints assigned to your restaurant
            </Typography>
          </Box>
          <Button
            variant="outlined" size="small"
            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem !important' }} />}
            onClick={() => { refetch(); refetchCounts() }}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', borderColor: '#e2e8f0', color: '#475569', '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}
          >
            Refresh
          </Button>
        </Stack>

        {/* Summary bar — always uses unfiltered data */}
        {allTicketsUnfiltered.length > 0 && <SummaryBar tickets={allTicketsUnfiltered} />}

        {/* Search + Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search ticket #, order #, or issue…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#94a3b8' }} /></InputAdornment>,
            }}
            sx={{
              width: { xs: '100%', sm: 280 },
              '& .MuiOutlinedInput-root': { borderRadius: '9px', bgcolor: '#fff', fontSize: '0.85rem' },
            }}
          />
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {FILTERS.map(f => {
              const count = f.key
                ? allTicketsUnfiltered.filter(t => t.status === f.key).length
                : allTicketsUnfiltered.length
              const isActive = statusFilter === f.key
              return (
                <Box
                  key={String(f.key)}
                  onClick={() => setStatusFilter(f.key)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    px: 1.25, py: 0.6, borderRadius: '20px', cursor: 'pointer',
                    bgcolor: isActive ? '#0f172a' : '#fff',
                    border: `1px solid ${isActive ? '#0f172a' : '#e2e8f0'}`,
                    transition: 'all 0.12s ease',
                    '&:hover': { bgcolor: isActive ? '#1e293b' : '#f8fafc' },
                  }}
                >
                  <Typography fontSize="0.78rem" fontWeight={isActive ? 700 : 500} color={isActive ? '#fff' : '#475569'}>
                    {f.label}
                  </Typography>
                  {count > 0 && (
                    <Box sx={{
                      bgcolor: isActive ? 'rgba(255,255,255,0.15)' : '#f1f5f9',
                      borderRadius: '10px', px: 0.7, py: 0.05,
                    }}>
                      <Typography fontSize="0.68rem" fontWeight={700} color={isActive ? '#fff' : '#64748b'}>
                        {count}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Stack>
        </Stack>

        {/* Table */}
        <Paper sx={{ borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
          <TableHeader />

          {loading && tickets.length === 0 ? (
            <Box py={6} display="flex" flexDirection="column" alignItems="center" gap={1.5}>
              <CircularProgress size={28} sx={{ color: '#94a3b8' }} />
              <Typography fontSize="0.85rem" color="#94a3b8">Loading tickets…</Typography>
            </Box>
          ) : tickets.length === 0 ? (
            <Box py={7} display="flex" flexDirection="column" alignItems="center" gap={1}>
              <SupportAgentIcon sx={{ fontSize: 36, color: '#e2e8f0' }} />
              <Typography fontSize="0.9rem" fontWeight={600} color="#94a3b8">
                {search || statusFilter ? 'No tickets match this filter' : 'No support tickets yet'}
              </Typography>
              <Typography fontSize="0.8rem" color="#cbd5e1">
                {search || statusFilter ? 'Try adjusting your search or filter.' : 'Customer complaints will appear here.'}
              </Typography>
            </Box>
          ) : (
            tickets.map((ticket, i) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                onClick={t => setSelectedTicketId(t.id)}
                isLast={i === tickets.length - 1}
              />
            ))
          )}

          {/* Footer */}
          {tickets.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9', bgcolor: '#fafafa' }}>
              <Typography fontSize="0.75rem" color="#94a3b8">
                Showing {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                {statusFilter ? ` · Filtered by: ${FILTERS.find(f => f.key === statusFilter)?.label}` : ''}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      <TicketDrawer
        ticket={selectedTicket}
        open={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        onRefetch={() => { refetch(); refetchCounts() }}
      />
    </Box>
  )
}
